import { Fragment, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Inbox } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Skeleton } from './misc';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
}

export function DataTable<T>({ columns, rows, loading, empty, rowKey, onRowClick, footer, expandedKey, renderExpanded }: {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  empty?: ReactNode;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  footer?: ReactNode;
  /** Clave de la fila desplegada (acordeón). Debe coincidir con `rowKey` de esa fila. */
  expandedKey?: string | null;
  /** Contenido del panel desplegado bajo la fila `expandedKey`. */
  renderExpanded?: (row: T) => ReactNode;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="table-dense w-full min-w-[640px]">
        <thead className="border-b border-border bg-muted/30">
          <tr>
            {columns.map((column) => (
              <th key={column.key} className={cn(column.headerClassName)}>{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 6 }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`}>
                  {columns.map((column) => (
                    <td key={column.key}><Skeleton className="h-4 w-full max-w-[160px]" /></td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                const key = rowKey(row);
                const expanded = Boolean(renderExpanded && expandedKey === key);
                return (
                  <Fragment key={key}>
                    <tr
                      className={cn(
                        onRowClick ? 'cursor-pointer' : undefined,
                        expanded ? 'bg-muted/30' : undefined
                      )}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {columns.map((column) => (
                        <td key={column.key} className={cn(column.className)}>{column.cell(row)}</td>
                      ))}
                    </tr>
                    {expanded ? (
                      // La celda del panel hereda el `whitespace-nowrap` de `.table-dense td`:
                      // `table-cell-wrap` lo anula para que el formulario no ensanche la tabla.
                      <tr className="bg-muted/10 hover:bg-muted/10">
                        <td colSpan={columns.length} className="table-cell-wrap p-0">{renderExpanded!(row)}</td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
        </tbody>
        {!loading && rows.length === 0 ? (
          <tfoot>
            <tr>
              <td colSpan={columns.length} className="px-3 py-14 text-center">
                {empty ?? (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Inbox className="size-6" />
                    <span className="text-xs">Sin resultados</span>
                  </div>
                )}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
      {footer}
    </div>
  );
}

export function Pagination({ page, totalPages, total, onPageChange }: {
  page: number; totalPages: number; total: number; onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-muted-foreground">
      <span>{total} resultados · página {page} de {totalPages}</span>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon-sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Anterior">
          <ChevronLeft />
        </Button>
        <Button variant="outline" size="icon-sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="Siguiente">
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}
